import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import DetalleTramiteModal from '../components/DetalleTramiteModal';
import Pagination from '../components/Pagination';
import './Profiles.css';

const PlateInventoryDetail = () => {
  const { user } = useContext(AuthContext);
  const { transito_id } = useParams();
  const navigate = useNavigate();
  const [plates, setPlates] = useState([]);
  const [transito, setTransito] = useState(null);
  const [concesionarios, setConcesionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [searchTerm, setSearchTerm] = useState('');

  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Estado para el modal de detalle de trámite
  const [selectedTramiteId, setSelectedTramiteId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Estado para el modal de asignación de placas
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({
    concesionario_id: '',
    vehicle_type: 'carro',
    start_plate: '',
    end_plate: ''
  });
  const [assignError, setAssignError] = useState('');
  const [assignSuccess, setAssignSuccess] = useState('');

  const queryParams = new URLSearchParams(window.location.search);
  const rangeId = queryParams.get('range_id');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const platesUrl = rangeId 
        ? `/api/transitos/${transito_id}/plates?range_id=${rangeId}`
        : `/api/transitos/${transito_id}/plates`;

      const [platesRes, transitosRes, concesionariosRes] = await Promise.all([
        axios.get(platesUrl),
        axios.get('/api/transitos'),
        axios.get('/api/concesionarios')
      ]);
      
      setPlates(platesRes.data);
      if (transito_id !== 'all') {
        const currentTransito = transitosRes.data.find(t => t.id === parseInt(transito_id));
        setTransito(currentTransito);
      } else {
        setTransito({ name: 'Todos los Tránsitos' });
      }
      setConcesionarios(concesionariosRes.data.filter(c => c.status === 'activo'));
      
      setError('');
    } catch (err) {
      setError('Error al cargar el inventario de placas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [transito_id, rangeId]);

  useEffect(() => {
    const isAllowed = user?.role === 'superadmin' || 
                      ['superadmin', 'admin_transito', 'admin_concesionario', 'admin concesionario', 'admin_punto_venta', 'concesionario', 'asesor'].includes(user?.profile_name) || 
                      ['superadmin', 'admin_transito', 'admin_concesionario', 'admin_punto_venta', 'admin', 'concesionario', 'asesor'].includes(user?.role) ||
                      !!user?.concesionario_id || !!user?.transito_id;
    if (!isAllowed) {
      navigate('/dashboard');
    }
    fetchData();
  }, [user, navigate, fetchData]);

  const handleAssignPlates = async (e) => {
    e.preventDefault();
    try {
      setAssignError('');
      setAssignSuccess('');
      const res = await axios.post('/api/transitos/assign-plates', {
        transito_id: parseInt(transito_id),
        concesionario_id: parseInt(assignForm.concesionario_id),
        vehicle_type: assignForm.vehicle_type,
        start_plate: assignForm.start_plate,
        end_plate: assignForm.end_plate
      });
      setAssignSuccess(res.data.message || 'Placas asignadas correctamente');
      fetchData();
      setTimeout(() => {
        setShowAssignModal(false);
        setAssignForm({ concesionario_id: '', vehicle_type: 'carro', start_plate: '', end_plate: '' });
        setAssignSuccess('');
      }, 1500);
    } catch (err) {
      setAssignError(err.response?.data?.message || 'Error al asignar placas');
    }
  };

  const handleUnassignPlate = async (plateId) => {
    if (!window.confirm('¿Está seguro de desasignar esta placa del concesionario?')) return;
    try {
      setError('');
      await axios.post('/api/transitos/unassign-plates', {
        plate_ids: [plateId]
      });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al desasignar placa');
    }
  };

  const filteredPlates = plates.filter(p => {
    const matchesStatus = filterStatus === 'todos' || p.status === filterStatus;
    const term = searchTerm.toLowerCase().trim();
    const matchesSearch = 
      !term ||
      p.plate_number?.toLowerCase().includes(term) ||
      p.transito_name?.toLowerCase().includes(term) ||
      p.vehicle_type?.toLowerCase().includes(term) ||
      (p.tramite_id && String(p.tramite_id).includes(term)) ||
      (p.cupl_id && String(p.cupl_id).includes(term)) ||
      p.concesionario_name?.toLowerCase().includes(term) ||
      p.consumed_by_concesionario_name?.toLowerCase().includes(term) ||
      p.consumed_by_name?.toLowerCase().includes(term);

    return matchesStatus && matchesSearch;
  });

  // Placas disponibles y sin concesionario asignado para el formulario de asignación
  const availablePlatesForForm = plates.filter(
    p => p.status === 'disponible' && !p.concesionario_id && p.vehicle_type === assignForm.vehicle_type
  );

  // Calcular cantidad de placas en el rango seleccionado
  const getSelectedRangeCount = () => {
    if (!assignForm.start_plate || !assignForm.end_plate) return 0;
    return availablePlatesForForm.filter(
      p => p.plate_number >= assignForm.start_plate && p.plate_number <= assignForm.end_plate
    ).length;
  };

  // Lógica de paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPlates.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  // Resetear página cuando cambien los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, searchTerm]);

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Navbar title={`🆔 Inventario Detallado: ${transito?.name || 'Cargando...'}${
          plates.length > 0 && rangeId
            ? plates[0].vehicle_type === 'moto' && plates[0].plate_number.length >= 6
              ? ` - Rango ${plates[0].plate_number.substring(0, 3)} - ${plates[0].plate_number.slice(-1)}`
              : ` - Rango ${plates[0].plate_number.substring(0, 3)}`
            : ''
        }`} />

        <div className="page-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
            <button onClick={() => navigate('/plates')} className="btn-secondary">
              ⬅️ Volver a Gestión de Placas
            </button>
            {user?.role === 'superadmin' && (
              <button onClick={() => setShowAssignModal(true)} className="btn-primary">
                ➕ Asignar Placas a Concesionario
              </button>
            )}
          </div>

          {error && <div className="error-banner">{error}</div>}

          <div className="page-header">
            <div className="header-title">
              <h2>Listado de Placas</h2>
              <p className="subtitle">Control de estado, asignaciones y trazabilidad por cada placa individual</p>
            </div>
            <div className="filters" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="🔍 Buscar por placa, tránsito, trámite..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', minWidth: '240px' }}
              />
              <select 
                value={filterStatus} 
                onChange={(e) => setFilterStatus(e.target.value)}
                className="btn-secondary"
                style={{ padding: '8px 16px', borderRadius: '8px' }}
              >
                <option value="todos">Todos los estados</option>
                <option value="disponible">✅ Disponibles</option>
                <option value="pre_asignada">⏳ Pre-asignadas</option>
                <option value="asignada">🔒 Asignadas (Usadas)</option>
                <option value="bloqueada">🚫 Bloqueadas</option>
              </select>
            </div>
          </div>

          {loading ? (
            <p className="loading">Cargando inventario...</p>
          ) : (
            <div className="table-wrapper">
              <div className="table-responsive">
                <table className="users-table">
                <thead>
                  <tr>
                    <th>Número de Placa</th>
                    {(transito_id === 'all' || user?.concesionario_id) && <th>Tránsito</th>}
                    <th>Tipo</th>
                    <th>Estado</th>
                    <th>Asignación / Consumo</th>
                    <th>Vínculo</th>
                    <th>Fecha de Uso</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(p => (
                    <tr key={p.id}>
                      <td><span className="plate-badge-sm" style={{ fontSize: '15px', padding: '6px 12px' }}>{p.plate_number}</span></td>
                      {(transito_id === 'all' || user?.concesionario_id) && <td><strong>{p.transito_name || transito?.name || 'Tránsito'}</strong></td>}
                      <td>
                        <span className={`vehicle-type-badge ${p.vehicle_type}`}>
                          {p.vehicle_type === 'moto' ? '🏍️ Moto' : 
                           p.vehicle_type === 'moto_carro' ? '🛺 Moto Carro' : 
                           '🚗 Carro'}
                        </span>
                      </td>
                      <td>
                        <span className={`status ${p.status}`}>
                          {p.status === 'disponible' ? 'Disponible' : 
                           p.status === 'pre_asignada' ? 'Pre-asignada' : 
                           p.status === 'asignada' ? 'Asignada' : p.status}
                        </span>
                      </td>
                      <td>
                        {user?.role === 'superadmin' ? (
                          <>
                            {p.concesionario_name ? (
                              <div style={{ fontSize: '13px' }}>
                                <span style={{ fontWeight: '600', color: '#1e3a8a' }}>Asig: </span>
                                {p.concesionario_name}{p.punto_venta_name ? ` (${p.punto_venta_name})` : ''}
                                <div style={{ fontSize: '11px', color: '#64748b' }}>por {p.assigned_by_name || 'sistema'}</div>
                              </div>
                            ) : null}
                            {p.consumed_by_name ? (
                              <div style={{ fontSize: '13px', marginTop: p.concesionario_name ? '4px' : '0' }}>
                                <span style={{ fontWeight: '600', color: '#16a34a' }}>Cons: </span>
                                {p.consumed_by_name} ({p.consumed_by_concesionario_name || 'Particular'}{p.consumed_by_punto_venta_name ? ` - ${p.consumed_by_punto_venta_name}` : ''})
                              </div>
                            ) : p.concesionario_name ? null : (
                              <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '13px' }}>Libre</span>
                            )}
                          </>
                        ) : (
                          p.concesionario_name || p.consumed_by_concesionario_name ? (
                            <div style={{ fontSize: '13px' }}>
                              <span style={{ fontWeight: '600', color: '#1e3a8a' }}>
                                TransiApp{p.punto_venta_name ? ` (${p.punto_venta_name})` : ''}
                              </span>
                            </div>
                          ) : p.consumed_by_name ? (
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#16a34a' }}>Consumo Local</div>
                          ) : (
                            <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '13px' }}>Libre</span>
                          )
                        )}
                      </td>
                      <td>
                        {p.tramite_id ? (
                          <button 
                            className="btn-link" 
                            onClick={() => {
                              setSelectedTramiteId(p.tramite_id);
                              setIsModalOpen(true);
                            }}
                            style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: '600', textDecoration: 'underline' }}
                          >
                            📁 Trámite #{p.tramite_id}
                          </button>
                        ) : p.cupl_id ? (
                          <span style={{ fontWeight: '600', color: '#64748b' }}>🧾 CUPL #{p.cupl_id}</span>
                        ) : (
                          <span className="no-info">—</span>
                        )}
                      </td>
                      <td>
                        {p.consumed_at ? new Date(p.consumed_at).toLocaleString() : 'N/A'}
                      </td>
                      <td>
                        {p.status === 'disponible' && p.concesionario_id && user?.role === 'superadmin' ? (
                          <button 
                            onClick={() => handleUnassignPlate(p.id)} 
                            className="btn-action btn-delete"
                            title="Quitar Asignación de Concesionario"
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            🔓 Desasignar
                          </button>
                        ) : (
                          <span className="no-info">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {filteredPlates.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No se encontraron placas con este estado.
                </div>
              )}

              <Pagination 
                currentPage={currentPage}
                totalItems={filteredPlates.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>
      </div>

      {showAssignModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <h3>Asignar Placas a Concesionario</h3>
            {assignError && <div className="error-banner" style={{ margin: '0 0 15px 0' }}>{assignError}</div>}
            {assignSuccess && <div className="success-banner" style={{ margin: '0 0 15px 0', padding: '10px', background: '#ecfdf5', color: '#065f46', borderRadius: '6px' }}>{assignSuccess}</div>}
            
            <form onSubmit={handleAssignPlates}>
              <div className="form-group">
                <label>Seleccionar Concesionario</label>
                <select 
                  value={assignForm.concesionario_id} 
                  onChange={(e) => setAssignForm({...assignForm, concesionario_id: e.target.value})}
                  required
                >
                  <option value="">Seleccione concesionario...</option>
                  {concesionarios.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Tipo de Vehículo</label>
                <select 
                  value={assignForm.vehicle_type} 
                  onChange={(e) => setAssignForm({...assignForm, vehicle_type: e.target.value, start_plate: '', end_plate: ''})}
                  required
                >
                  <option value="carro">🚗 Carro</option>
                  <option value="moto">🏍️ Moto</option>
                  <option value="moto_carro">🛺 Moto Carro</option>
                </select>
              </div>

              <div className="form-group">
                <label>Placa Inicial (Disponible)</label>
                <select 
                  value={assignForm.start_plate} 
                  onChange={(e) => setAssignForm({...assignForm, start_plate: e.target.value, end_plate: ''})}
                  required
                >
                  <option value="">Seleccione placa inicial...</option>
                  {availablePlatesForForm.map(p => (
                    <option key={p.id} value={p.plate_number}>{p.plate_number}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Placa Final (Disponible)</label>
                <select 
                  value={assignForm.end_plate} 
                  onChange={(e) => setAssignForm({...assignForm, end_plate: e.target.value})}
                  required
                  disabled={!assignForm.start_plate}
                >
                  <option value="">Seleccione placa final...</option>
                  {availablePlatesForForm
                    .filter(p => !assignForm.start_plate || p.plate_number >= assignForm.start_plate)
                    .map(p => (
                      <option key={p.id} value={p.plate_number}>{p.plate_number}</option>
                    ))}
                </select>
              </div>

              {getSelectedRangeCount() > 0 && (
                <div style={{ background: '#eff6ff', padding: '12px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #bfdbfe', color: '#1e40af', fontSize: '14px', fontWeight: '500' }}>
                  ℹ️ Se asignarán {getSelectedRangeCount()} placas en este rango (inclusive).
                </div>
              )}

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowAssignModal(false);
                    setAssignForm({ concesionario_id: '', vehicle_type: 'carro', start_plate: '', end_plate: '' });
                    setAssignError('');
                  }} 
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={!assignForm.start_plate || !assignForm.end_plate}>
                  Confirmar Asignación
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DetalleTramiteModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        tramiteId={selectedTramiteId}
        onUpdate={fetchData}
      />
    </div>
  );
};

export default PlateInventoryDetail;
